import { useState } from "react";
import type { CSSProperties } from "react";
import { buildAuthHeaders } from "../../api/client.ts";
import { downloadExportByPath } from "../../api/fileUpload.ts";
import {
  ATTACHMENT_LABEL,
  ATTACHMENT_SIDE,
  ATTACHMENT_TYPES,
  CLOSE_SHEET_LABEL,
  CLOSE_SHEET_TYPES,
  getStatementSheet,
  isCloseSheet,
  type StatementSheetPage,
  type StatementSheetType,
} from "../../api/statementConfig.ts";
import { BuildStates } from "../../types/grid.ts";

/**
 * Detail-sheet downloads for one selected invoice or bookkeeping close, shared by the two
 * statement modals (the same way payoutTargetPanel is shared).
 *
 * The file is the SAME one StatementInvoiceProcessor attaches to the Odoo move — one library
 * builds both, one exporter renders both — so this is the evidence the customer already holds,
 * not a second opinion about it. It is offered for every sheet that is COMPUTABLE for the item,
 * not only the ones the config's `invoiceAttachments` asked for, so a period that was never
 * pushed still answers "what would have been generated".
 *
 * COLLAPSED BY DEFAULT, and one line when closed: this is a side door, not the point of either
 * modal (GP, 2026-09-04 — the first version put seven buttons in the middle of the invoice info).
 * The disclosure is the same idiom the bookkeeping modal already uses for its raw JSON.
 *
 * Formatting is stated, not chosen: no culture or timezone control, because a download formatted
 * sv-SE would differ from the attached file in decimal separators while every figure matched, and
 * whoever diffed the two would have to work out which kind of difference they were looking at.
 */
export function StatementSheetsPanel({ page, itemId }: { page: StatementSheetPage; itemId: number }) {
  const [open, setOpen] = useState(false);
  const [fileType, setFileType] = useState<"XLSX" | "CSV">("XLSX");
  const [busy, setBusy] = useState<StatementSheetType | null>(null);
  const [message, setMessage] = useState<{ kind: "info" | "error"; text: string } | null>(null);

  const available = PANEL_SHEETS.filter((t) => unavailableReason(page, t) == null);

  async function download(sheet: StatementSheetType) {
    setBusy(sheet);
    setMessage(null);
    try {
      const res = await getStatementSheet({ Name: page, Sheet: sheet, ItemID: itemId, FileType: fileType });
      // The server's message is the sheet builder's OWN reason — "no refunds in the period",
      // "no daily closes for the operator in the period" — so it is worth showing verbatim
      // rather than replacing with a generic failure.
      if (res.bError || !res.oData) {
        setMessage({ kind: "info", text: res.sMessage || "That sheet has nothing to report for this item." });
        return;
      }
      const info = res.oData;
      if (info.BuildState !== BuildStates.Done || !info.DownloadPath) {
        setMessage({ kind: "error", text: "The sheet was built but no download link came back. Check Exported Reports." });
        return;
      }
      const headers = await buildAuthHeaders();
      await downloadExportByPath(info.DownloadPath, `${sheet}.${fileType.toLowerCase()}`, headers);
    } catch (err) {
      setMessage({ kind: "error", text: err instanceof Error ? err.message : "The download failed." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ borderTop: "1px solid var(--color-bg-muted)", paddingTop: "var(--space-2)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)" }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="statement-detail-sheets"
          style={toggle}
          title="The same files attached to the Odoo document"
        >
          {open ? "▾" : "▸"} Detail sheets
        </button>
        <span style={{ ...muted, fontSize: "var(--text-xs)" }}>
          {available.length} available{open ? " · invariant formatting, dates as closed" : ""}
        </span>
        {open && (
          <span style={{ marginLeft: "auto", fontSize: "var(--text-xs)" }}>
            {(["XLSX", "CSV"] as const).map((ft, i) => (
              <span key={ft}>
                {i > 0 && <span style={muted}> · </span>}
                <button
                  type="button"
                  onClick={() => setFileType(ft)}
                  aria-pressed={fileType === ft}
                  style={fileType === ft ? formatOn : formatOff}
                >
                  {ft.toLowerCase()}
                </button>
              </span>
            ))}
          </span>
        )}
      </div>

      {open && (
        <div id="statement-detail-sheets" style={{ marginTop: "var(--space-1)" }}>
          {message && (
            <div style={message.kind === "error" ? noteError : noteInfo}>{message.text}</div>
          )}
          <div style={grid}>
            {PANEL_SHEETS.map((sheet) => {
              const reason = unavailableReason(page, sheet);
              const label = SHORT_LABEL[sheet];
              if (reason) {
                return (
                  <span key={sheet} style={{ ...muted, fontSize: "var(--text-sm)" }} title={reason}>
                    {label}
                  </span>
                );
              }
              return (
                <button
                  key={sheet}
                  type="button"
                  disabled={busy != null}
                  onClick={() => void download(sheet)}
                  style={link}
                  title={sheetTitle(sheet)}
                >
                  {label}{busy === sheet ? " …" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact names for the list. `ATTACHMENT_LABEL` is the descriptive form and stays as the
 *  tooltip — it is written for the config editor, where a sheet has to be recognised from its
 *  name alone; here the item is already on screen. */
export const SHORT_LABEL: Record<StatementSheetType, string> = {
  kioskTechFee: "Technical fee",
  ipgDetail: "IPG detail",
  feeCalculation: "Fee calculation",
  kioskInventory: "Kiosk inventory",
  collectionDetail: "Collection detail",
  refundDetail: "Refund detail",
  vatSummary: "VAT summary",
  unpaidDetail: "Unpaid",
  accrualDetail: "Accrual detail",
};

/** Everything the panel lists: the attachment sheets, then the close-only ones. */
export const PANEL_SHEETS: readonly StatementSheetType[] = [...ATTACHMENT_TYPES, ...CLOSE_SHEET_TYPES];

/** The tooltip: the descriptive label, plus which document an attachment sheet rides with. A
 *  close-only sheet rides with none, so it says what it explains instead. */
function sheetTitle(sheet: StatementSheetType): string {
  if (isCloseSheet(sheet)) return `${CLOSE_SHEET_LABEL[sheet]} — explains this close's ledger posting`;
  const side = ATTACHMENT_SIDE[sheet];
  return `${ATTACHMENT_LABEL[sheet]} — ${side === "both" ? "either document" : `${side} side`}`;
}

/**
 * Why a sheet is not offered for this subject, or null when it is.
 *
 * An invoice offers every attachment sheet and none of the close-only ones: those explain a day's
 * ledger posting, which an invoice is not. A bookkeeping CLOSE is ONE DAY, so the fee sheets are out:
 * they are built from the invoice period's aggregates, and a close has none.
 *
 * The server decides this too (`StatementSheetUnavailableReason`, and the builders refuse anyway
 * with their own reason). This copy exists so a row can be shown greyed with an explanation instead
 * of inviting a click that comes back empty — the server stays the authority.
 */
export function unavailableReason(page: StatementSheetPage, sheet: StatementSheetType): string | null {
  if (isCloseSheet(sheet)) {
    return page === "statementInvoice" ? "Explains a bookkeeping close — open it from the close." : null;
  }
  if (page === "statementInvoice") return null;
  return ATTACHMENT_SIDE[sheet] === "fee"
    ? "Built from the invoice period's aggregates — a bookkeeping close is one day."
    : null;
}

const muted: CSSProperties = { color: "var(--color-text-muted)" };
const toggle: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontSize: "var(--text-xs)",
  fontWeight: 600,
  color: "var(--color-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--tracking-wider)",
};
const link: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontSize: "var(--text-sm)",
  color: "var(--color-link, var(--color-primary))",
  textAlign: "left",
};
const formatOn: CSSProperties = { ...link, fontSize: "var(--text-xs)", fontWeight: 600, textDecoration: "underline" };
const formatOff: CSSProperties = { ...link, fontSize: "var(--text-xs)", color: "var(--color-text-muted)" };
const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "var(--space-1) var(--space-3)",
};
const noteInfo: CSSProperties = {
  fontSize: "var(--text-xs)",
  color: "var(--color-text-muted)",
  marginBottom: "var(--space-1)",
};
const noteError: CSSProperties = { ...noteInfo, color: "var(--color-danger)" };

/** The disclosure's styles, shared with the platform invoice's detail sheets (platformBilling/billingSheetsPanel). */
export const sheetPanelStyles = { muted, toggle, link, formatOn, formatOff, noteInfo, noteError } as const;
